import { useMediaQuery } from '@jouzen/ecom-components/hooks/useMediaQuery';
import { BREAKPOINTS } from '@jouzen/ecom-components/v3/consts/breakpoints';

import SectionHeading, {
  type SectionHeadingProps,
} from '@/app/components/SectionHeading';

import type { SectionContentProps } from './SectionContent';

export interface SectionContentTitleProps
  extends Pick<SectionContentProps, 'heading'> {
  readonly isActive: boolean;
}

interface GetAnimationPropsParams {
  isActive: boolean;
  isLargeBreakpoint: boolean;
}

export const getEmAnimationProps = ({
  isActive,
  isLargeBreakpoint,
}: GetAnimationPropsParams): SectionHeadingProps['emAnimationProps'] => {
  const isActiveOrMobile = !isLargeBreakpoint || isActive;

  return {
    animate: {
      filter: 'blur(0px)',
      transitionEnd: {
        filter: isActiveOrMobile ? 'blur(0px)' : 'blur(10px)',
      },
    },
    initial: {
      filter: 'blur(10px)',
    },
    transition: {
      delay: 0.5,
      duration: 1.25,
      type: 'tween',
    },
  };
};

export const getWrapperAnimationProps = ({
  isActive,
  isLargeBreakpoint,
}: GetAnimationPropsParams): SectionHeadingProps['wrapperAnimationProps'] => {
  const isActiveOrMobile = !isLargeBreakpoint || isActive;
  const initialY = !isLargeBreakpoint ? 0 : 100;

  return {
    animate: {
      filter: 'blur(0px)',
      opacity: isActiveOrMobile ? 1 : 0,
      y: 0,
      transitionEnd: {
        filter: isActiveOrMobile ? 'blur(0px)' : 'blur(5px)',
        opacity: isActiveOrMobile ? 1 : 0,
        y: isActiveOrMobile ? 0 : 100,
      },
    },
    initial: {
      filter: 'blur(5px)',
      opacity: isActiveOrMobile ? 1 : 0,
      y: initialY,
    },
    transition: {
      delay: 0.5,
      duration: 0.75,
      type: 'tween',
    },
  };
};

const SectionContentTitle = ({
  heading,
  isActive,
}: SectionContentTitleProps) => {
  const isLargeBreakpoint = useMediaQuery(
    `(min-width: ${BREAKPOINTS.large}px)`,
  );
  const getAnimationPropsParams: GetAnimationPropsParams = {
    isActive,
    isLargeBreakpoint: Boolean(isLargeBreakpoint),
  };

  return (
    <SectionHeading
      animated
      className="relative z-20 mb-4"
      content={heading}
      emAnimationProps={getEmAnimationProps(getAnimationPropsParams)!}
      indentation="pl-14 lg:pl-24"
      multiline
      shouldAnimate={isActive || !isLargeBreakpoint}
      typographyProps={{
        className:
          'leading-tight tracking-tighter text-[2.5rem] xl:text-[3.25rem] xxl:text-[4.25rem]',
        color: 'inherit',
        Element: 'h3',
        variant: 'h3',
      }}
      wrapperAnimationProps={getWrapperAnimationProps(getAnimationPropsParams)!}
    />
  );
};

export default SectionContentTitle;
